import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Users, Plus, Spinner, Eye, Pencil, Trash, Circuitry,
  MagnifyingGlass, Buildings,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getErrorMessage } from "../lib/utils";
import { useTranslation } from "react-i18next";
import Pagination from "../components/Pagination";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCustomersPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [subscriptions, setSubscriptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [formData, setFormData] = useState({
    name: "", email: "", password: "", phone: "", company_name: "",
  });

  const fetchData = useCallback(async () => {
    try {
      const custRes = await axios.get(`${API_URL}/api/admin/customers`);
      setCustomers(custRes.data);
      const subs = {};
      for (const c of custRes.data) {
        try {
          const subRes = await axios.get(`${API_URL}/api/admin/customer/${c.id}/subscription`);
          subs[c.id] = subRes.data;
        } catch { /* no subscription */ }
      }
      setSubscriptions(subs);
    } catch { toast.error("Musteriler yuklenirken hata olustu"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/customers`, formData);
      toast.success("Musteri basariyla olusturuldu");
      setDialogOpen(false);
      setFormData({ name: "", email: "", password: "", phone: "", company_name: "" });
      fetchData();
    } catch (error) { toast.error(getErrorMessage(error)); }
    finally { setFormLoading(false); }
  };

  const handleDeleteCustomer = async (customerId, customerName) => {
    if (!window.confirm(`"${customerName}" musterisini silmek istediginize emin misiniz?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/customers/${customerId}`);
      toast.success("Musteri silindi");
      fetchData();
    } catch (error) { toast.error(getErrorMessage(error)); }
  };

  const filtered = customers.filter(c =>
    !search || c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase()) ||
    c.company_name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Spinner size={32} className="animate-spin text-indigo-600" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-customers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">{t("customers.title")}</h1>
          <p className="text-slate-500 mt-1">{t("customers.subtitle")}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-customer-btn">
              <Plus size={18} className="mr-2" /> {t("customers.new")}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t("admin.add_customer")}</DialogTitle>
              <DialogDescription>{t("admin.add_customer_desc")}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateCustomer}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t("common.name")}</Label>
                  <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="customer-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company_name">{t("admin.company_name_label")}</Label>
                  <Input id="company_name" value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} data-testid="customer-company-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{t("common.email")}</Label>
                  <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="customer-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">{t("common.password")}</Label>
                  <Input id="password" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required data-testid="customer-password-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">{t("common.phone")}</Label>
                  <Input id="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="customer-phone-input" />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={formLoading} data-testid="customer-submit-btn">
                  {formLoading ? t("common.creating") : t("common.create")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder={t("customers.search_placeholder")}
          value={search} onChange={(e) => setSearch(e.target.value)}
          className="pl-9 h-9"
          data-testid="customer-search"
        />
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center">
              <Users size={20} weight="duotone" className="text-indigo-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">{t("customers.total")}</p>
              <p className="text-xl font-bold text-slate-900">{customers.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
              <Buildings size={20} weight="duotone" className="text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">{t("customers.total_apartment")}</p>
              <p className="text-xl font-bold text-slate-900">{customers.reduce((a, c) => a + (c.apartments_count || 0), 0)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
              <Circuitry size={20} weight="duotone" className="text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">{t("customers.total_meter")}</p>
              <p className="text-xl font-bold text-slate-900">{customers.reduce((a, c) => a + (c.devices_count || 0), 0)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
              <Users size={20} weight="duotone" className="text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">{t("customers.total_tenant")}</p>
              <p className="text-xl font-bold text-slate-900">{customers.reduce((a, c) => a + (c.tenants_count || 0), 0)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Customer Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="text-center py-12">
              <Users size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">{search ? t("customers.no_result") : t("customers.no_data")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>{t("common.customer")}</TableHead>
                    <TableHead className="hidden md:table-cell">{t("common.company")}</TableHead>
                    <TableHead>{t("common.email")}</TableHead>
                    <TableHead className="hidden lg:table-cell">{t("common.phone")}</TableHead>
                    <TableHead>{t("common.apartment")}</TableHead>
                    <TableHead>{t("common.meter")}</TableHead>
                    <TableHead>{t("common.tenant")}</TableHead>
                    <TableHead className="hidden lg:table-cell">{t("customers.pos_mode")}</TableHead>
                    <TableHead>{t("common.subscription")}</TableHead>
                    <TableHead className="hidden md:table-cell">{t("common.register_date")}</TableHead>
                    <TableHead className="w-28">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.slice((page - 1) * pageSize, page * pageSize).map((customer) => {
                    const sub = subscriptions[customer.id];
                    const subData = sub?.subscription;
                    const isActive = subData?.status === "active";
                    const isExpired = isActive && new Date(subData.end_date) < new Date();
                    return (
                      <TableRow key={customer.id} data-testid={`customer-row-${customer.id}`} className="hover:bg-slate-50">
                        <TableCell className="font-medium">{customer.name}</TableCell>
                        <TableCell className="text-slate-500 hidden md:table-cell">{customer.company_name || "-"}</TableCell>
                        <TableCell className="text-sm">{customer.email}</TableCell>
                        <TableCell className="text-sm hidden lg:table-cell">{customer.phone || "-"}</TableCell>
                        <TableCell>
                          <Badge className="bg-indigo-100 text-indigo-700">{customer.apartments_count || 0}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-purple-100 text-purple-700">{sub?.device_count ?? customer.devices_count ?? 0}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-emerald-100 text-emerald-700">{customer.tenants_count || 0}</Badge>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          <Badge className={
                            customer.payment_mode === "platform_pos" ? "bg-indigo-100 text-indigo-700" :
                            customer.payment_mode === "customer_pos" ? "bg-amber-100 text-amber-700" :
                            "bg-slate-100 text-slate-600"
                          }>
                            {customer.payment_mode === "platform_pos" ? t("customers.platform_pos") :
                             customer.payment_mode === "customer_pos" ? t("customers.own_pos") : t("customers.manual")}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {isActive && !isExpired ? (
                            <Badge className="bg-emerald-100 text-emerald-700">{t("common.active")}</Badge>
                          ) : isExpired ? (
                            <Badge className="bg-red-100 text-red-700">{t("customers.expired")}</Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-500">{t("customers.none")}</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500 hidden md:table-cell">
                          {new Date(customer.created_at).toLocaleDateString("tr-TR")}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="ghost" className="text-indigo-600" onClick={() => navigate(`/admin/customers/${customer.id}`)}
                              data-testid={`view-customer-${customer.id}`} title={t("common.detail")}>
                              <Eye size={16} />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-amber-600" onClick={() => navigate(`/admin/customers/${customer.id}?edit=true`)}
                              data-testid={`edit-customer-${customer.id}`} title={t("common.edit")}>
                              <Pencil size={16} />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteCustomer(customer.id, customer.name)}
                              data-testid={`delete-customer-${customer.id}`} title={t("common.delete")}>
                              <Trash size={16} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          {filtered.length > pageSize && (
            <div className="p-4">
              <Pagination total={filtered.length} page={page} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCustomersPage;
